// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the live side of mail hidden from Chats. The rules are a synced
// field of KonvoCalendarPrefs; this reads them for the chat lists (Chats,
// the Rail, Home) and adds or removes one optimistically. Mail itself never
// asks, so it keeps listing every mail.

import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import KonvoMail, {type KonvoMailMessage} from '@app/features/konvo_mail/state/KonvoMail';
import {
	hiddenFromChats,
	MAIL_CHAT_HIDE_MAX_RULES,
	type MailChatHideRule,
	type MailChatHideTarget,
	normalizeRule,
	sameRule,
	visibleInChats,
} from '@app/features/konvo_mail/utils/MailChatHiding';
import {makeAutoObservable} from 'mobx';

class MailChatHideStore {
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	get rules(): ReadonlyArray<MailChatHideRule> {
		return KonvoCalendarPrefs.mailChatHide;
	}

	/**
	 * The inbox as the chat lists see it. Hidden mails are dropped before the
	 * lists group them into conversations, so a conversation of only hidden
	 * mails disappears and a mixed one is headed by its newest shown mail.
	 */
	get chatInbox(): ReadonlyArray<KonvoMailMessage> {
		return visibleInChats(KonvoMail.inboxMessages, this.rules);
	}

	/**
	 * Inbox unread mail as the chat lists count it: the server's inbox count less
	 * the hidden unread mails that are loaded (the only ones Konvo can see).
	 */
	get chatUnreadCount(): number {
		if (this.rules.length === 0) return KonvoMail.unreadCount;
		const hiddenUnread = KonvoMail.inboxMessages.filter(
			(message) => !message.is_read && !message.is_draft && hiddenFromChats(message, this.rules),
		).length;
		return Math.max(0, KonvoMail.unreadCount - hiddenUnread);
	}

	isHidden(message: MailChatHideTarget): boolean {
		return hiddenFromChats(message, this.rules);
	}

	visible<T extends MailChatHideTarget>(messages: ReadonlyArray<T>): ReadonlyArray<T> {
		return visibleInChats(messages, this.rules);
	}

	has(rule: MailChatHideRule): boolean {
		return this.rules.some((existing) => sameRule(existing, rule));
	}

	/** Adds a rule (newest last); false when it is invalid, already there, or the list is full. */
	add(rule: MailChatHideRule): boolean {
		const normalized = normalizeRule(rule);
		if (!normalized || this.has(normalized) || this.rules.length >= MAIL_CHAT_HIDE_MAX_RULES) return false;
		void KonvoCalendarPrefs.load();
		void KonvoCalendarPrefs.save({mailChatHide: [...this.rules, normalized]});
		return true;
	}

	remove(rule: MailChatHideRule): void {
		if (!this.has(rule)) return;
		void KonvoCalendarPrefs.load();
		void KonvoCalendarPrefs.save({mailChatHide: this.rules.filter((existing) => !sameRule(existing, rule))});
	}
}

export default new MailChatHideStore();
